import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import NewsCard from '../components/UI/NewsCard';
import { useI18n } from '../i18n/useI18n';

interface NewsArticle {
  id: string;
  title: string;
  summary: string;
  category: string;
  imageUrl: string | null;
  biasScore: number;
  noticiabilityTotal: number;
  scoreJson: { impact: number; reach: number; duration: number };
  sourceName: string;
  publishedAt: string;
}

const CategoryPage: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const { t } = useI18n();
  
  const { data: newsData, isLoading, error } = useQuery({
    queryKey: ['category-news', category],
    queryFn: async () => {
      const response = await axios.get(`/api/v1/news/category/${encodeURIComponent(category || '')}`);
      return response.data;
    },
    enabled: !!category
  });

  const categoryIcons: Record<string, string> = {
    'Geopolitics': '🌍',
    'Economy': '💰',
    'Climate': '🌱',
    'Technology': '🤖',
    'Health': '🧬',
    'Disasters': '🚨',
    'Migration': '👥',
  };

  const cat = category || '';
  const icon = categoryIcons[cat] || '📰';
  const fullName = t(`cat.full.${cat}`) !== `cat.full.${cat}` ? t(`cat.full.${cat}`) : cat;
  const desc = t(`cat.desc.${cat}`) !== `cat.desc.${cat}` ? t(`cat.desc.${cat}`) : '';

  const articles: NewsArticle[] = newsData?.data || [];

  if (error) {
    return (
      <div className="container-news py-12">
        <div className="text-center">
          <h2 className="font-playfair text-2xl font-bold text-primary-900 mb-4">{t('catpage.errorTitle')}</h2>
          <p className="text-primary-600">{t('catpage.errorDesc')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Category Header */}
      <section className="bg-primary-50 border-b border-primary-200">
        <div className="container-news py-16">
          <div className="text-center">
            <div className="text-6xl mb-4">{icon}</div>
            <h1 className="headline-main mb-6">{fullName}</h1>
            {desc && <p className="text-xl text-primary-700 max-w-3xl mx-auto leading-relaxed">{desc}</p>}
            <div className="mt-8 flex items-center justify-center space-x-8 text-primary-600">
              <div className="text-center">
                <div className="text-2xl font-bold text-accent-600">{articles.length}</div>
                <div className="text-sm">{t('catpage.articles')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-accent-600">
                  {articles.reduce((sum, a) => sum + a.noticiabilityTotal, 0).toFixed(0)}
                </div>
                <div className="text-sm">{t('catpage.totalImpact')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-accent-600">
                  {articles.length > 0 ? (articles.reduce((sum, a) => sum + a.biasScore, 0) / articles.length).toFixed(1) : '0.0'}
                </div>
                <div className="text-sm">{t('catpage.avgBias')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="container-news py-12">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="news-card">
                <div className="loading-skeleton h-48 mb-4"></div>
                <div className="p-6">
                  <div className="loading-skeleton h-4 mb-2"></div>
                  <div className="loading-skeleton h-4 w-3/4 mb-4"></div>
                  <div className="loading-skeleton h-3 mb-2"></div>
                  <div className="loading-skeleton h-3 w-5/6"></div>
                </div>
              </div>
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-6">{icon}</div>
            <h3 className="font-playfair text-2xl font-bold text-primary-900 mb-4">
              {t('catpage.noArticles')} {fullName}
            </h3>
            <p className="text-primary-600 max-w-md mx-auto">{t('catpage.noArticlesDesc')}</p>
          </div>
        ) : (
          <>
            {/* High Impact */}
            {articles.filter(a => a.noticiabilityTotal > 400).length > 0 && (
              <div className="mb-12">
                <h2 className="headline-section mb-8">{t('catpage.highImpactStories')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {articles.filter(a => a.noticiabilityTotal > 400).slice(0, 2).map(a => (
                    <NewsCard key={a.id} article={a} featured={true} />
                  ))}
                </div>
              </div>
            )}

            {/* All Articles */}
            <div className="mb-12">
              <h2 className="headline-section mb-8">
                {articles.filter(a => a.noticiabilityTotal > 400).length > 0 ? t('catpage.moreStories') : t('catpage.latestStories')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles
                  .filter(a => a.noticiabilityTotal <= 400 || articles.filter(x => x.noticiabilityTotal > 400).length === 0)
                  .map(a => <NewsCard key={a.id} article={a} />)}
              </div>
            </div>

            {/* Stats */}
            <div className="bg-primary-50 newspaper-border p-8">
              <h3 className="font-playfair text-xl font-bold text-primary-900 mb-4">
                {fullName} — {t('catpage.insights')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-2xl font-bold text-accent-600 mb-2">{articles.filter(a => a.noticiabilityTotal > 400).length}</div>
                  <div className="text-primary-700">{t('catpage.highImpactStories')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-accent-600 mb-2">{articles.filter(a => a.biasScore <= 3).length}</div>
                  <div className="text-primary-700">{t('catpage.lowBiasArticles')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-accent-600 mb-2">{new Set(articles.map(a => a.sourceName)).size}</div>
                  <div className="text-primary-700">{t('catpage.uniqueSources')}</div>
                </div>
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default CategoryPage;